import { Input } from '../../../../components/Input'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from '../../../../hooks/hooks'
import { TQueryParams } from '../../../../components/Table/types'
import { setLoading } from '../../../../redux/store/common/slice'
import { cleanParams } from '../../../../utils/common'
import { useDispatch, useSelector } from 'react-redux'
import useInfiniteData from '../../../../hooks/useInfiniteData'
import { LoadingOutlined } from '@ant-design/icons'
import InfiniteList, {
  ILRenderItem
} from '../../../../components/InfiniteList/InfiniteList'
import { EmptyState } from './EmptyState'
import { getIsLoading } from '../../../../redux/store/common/getters'
import { fetchCommunityProcessing } from '../../api'
import { TCommunityProcessingItem } from '../../types'
import { Item } from './Item'

export const CommunityProcessing = () => {
  const dispatch = useDispatch()
  const isLoading = useSelector(getIsLoading)
  const firstLoadRef = useRef(true)
  const [searchValue, setSearchValue] = useState('')
  const [processingData, setProcessingData] = useState<
    TCommunityProcessingItem[]
  >([])
  const [total, setTotal] = useState(0)
  const [activeKey, setActiveKey] = useState<string[]>([])

  const debouncedSearch = useDebounce(searchValue, 500)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const getMoreData = useCallback(async (args: TQueryParams) => {
    const resp = await fetchCommunityProcessing({ params: { ...args } })
    if (resp.data?.results) {
      setProcessingData((prev) => [...prev, ...resp.data.results])
    }
  }, [])

  const loadMoreGpo = useInfiniteData({
    total: total,
    limit: 25,
    loadMore: getMoreData
  })

  const getProcessingData = useCallback(
    async (params: TQueryParams) => {
      try {
        dispatch(setLoading(true))
        if (firstLoadRef.current) {
          firstLoadRef.current = false
        }
        const cleanedParams = cleanParams(params)
        const response = await fetchCommunityProcessing({
          params: { ...cleanedParams }
        })
        setProcessingData(response.data.results)
        setTotal(response.data?.count)
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getProcessingData({ search: debouncedSearch, limit: 25, offset: 0 })
      },
      firstLoadRef.current ? 0 : 500
    )
  }, [debouncedSearch, getProcessingData])

  const callback = useCallback(() => {
    getProcessingData({ search: debouncedSearch, limit: 25, offset: 0 })
  }, [debouncedSearch, getProcessingData])

  const renderItem = useCallback(
    ({ item }: ILRenderItem<TCommunityProcessingItem>) => (
      <Item
        item={item}
        activeKey={activeKey}
        onChange={setActiveKey}
        callback={callback}
      />
    ),
    [activeKey, callback]
  )

  const Loader = useMemo(
    () => <LoadingOutlined className="chat-list-feature-list__spinner" spin />,
    []
  )

  return (
    <div className="community-proccesing-container">
      <div className="table-wrapper__header">
        <Input
          className="allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: 'Search by community',
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
      </div>
      <InfiniteList<TCommunityProcessingItem>
        items={processingData}
        loader={Loader}
        renderItem={renderItem}
        loading={isLoading}
        keyExtractor={(item) => item.uuid}
        hasNextPage={loadMoreGpo.hasNextPage}
        listEmptyComponent={<EmptyState />}
        onLoadMore={loadMoreGpo.handleLoadMore}
        className="gpo-container__list"
      />
    </div>
  )
}
